import * as Dialog from "@radix-ui/react-dialog";
import { useNavigate } from "react-router";
import Button from "@/components/ui/button";
import type { UserRole } from "@/modules/membership";
import { PLAN_GROWTH } from "@/modules/plan";

export type UpgradeContext =
	| "reviews_limit"
	| "widgets_limit"
	| "premium_widget"
	| "widget_customization"
	| "forms_limit"
	| "team_members_limit"
	| "branding_removal"
	| "wall_of_love"
	| "dynamic_walls"
	| "dynamic_widgets"
	| "reviews_per_wall_limit"
	| "reviews_per_widget_limit"
	| "custom_domain"
	| "analytics"
	| "api_access"
	| "integrations"
	| "branding_customization"
	| "general";

interface UpgradeToProModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	context?: UpgradeContext;
	customMessage?: string;
	customTitle?: string;
	workspaceId?: string;
	reviewLimit?: number;
	userRole?: UserRole;
	onMaybeLater?: () => void;
}

const CONTEXT_MESSAGES: Record<UpgradeContext, { title: string; message: (limit?: number, isAdmin?: boolean) => string }> = {
	reviews_limit: {
		title: "Review Limit Reached",
		message: (limit?: number, isAdmin?: boolean) =>
			isAdmin
				? `You've reached the limit of ${limit ?? 20} reviews on the free plan. Upgrade to Growth plan to collect unlimited reviews and unlock all features.`
				: `This brand has reached the limit of ${limit ?? 20} reviews. Please contact your workspace admin to upgrade the plan.`,
	},
	widgets_limit: {
		title: "Widget Limit Reached",
		message: (_limit?: number, isAdmin?: boolean) =>
			isAdmin
				? "You've reached the widget limit on the free plan. Upgrade to Growth plan to create unlimited widgets and customize them to match your brand."
				: "This brand has reached the widget limit. Please contact your workspace admin to upgrade the plan.",
	},
	premium_widget: {
		title: "Premium Widget",
		message: (_limit?: number, isAdmin?: boolean) =>
			isAdmin
				? "This widget template is available on the Growth plan. Upgrade to unlock this template and access all premium features."
				: "This widget template requires a plan upgrade. Please contact your workspace admin to unlock it.",
	},
	widget_customization: {
		title: "Customize Widgets",
		message: (_limit?: number, isAdmin?: boolean) =>
			isAdmin
				? "Advanced widget customization is available on the Growth plan. Upgrade to personalize your widgets to match your brand."
				: "Widget customization is not available on this plan. Please contact your workspace admin to upgrade.",
	},
	forms_limit: {
		title: "Form Limit Reached",
		message: (_limit?: number, isAdmin?: boolean) =>
			isAdmin
				? "You've reached the form limit on the free plan. Upgrade to Growth plan to create unlimited collection forms with advanced customization options."
				: "This brand has reached the form limit. Please contact your workspace admin to upgrade the plan.",
	},
	team_members_limit: {
		title: "Team Limit Reached",
		message: (_limit?: number, isAdmin?: boolean) =>
			isAdmin
				? "You've reached the team member limit on the free plan. Upgrade to Growth plan to add unlimited team members and collaborate seamlessly."
				: "This brand has reached the team member limit. Please contact your workspace admin to upgrade the plan.",
	},
	branding_removal: {
		title: "Remove Branding",
		message: (_limit?: number, isAdmin?: boolean) =>
			isAdmin
				? "Remove the 'Powered by Say About Us' branding from your widgets. Upgrade to Growth plan for a completely white-label experience."
				: "To remove branding, please contact your workspace admin to upgrade the plan.",
	},
	wall_of_love: {
		title: "Wall of Love",
		message: (_limit?: number, isAdmin?: boolean) =>
			isAdmin
				? "Create beautiful Walls of Love to showcase your testimonials. Upgrade to Growth plan to unlock this feature and more."
				: "To create Walls of Love, please contact your workspace admin to upgrade the plan.",
	},
	dynamic_walls: {
		title: "Dynamic Walls",
		message: (_limit?: number, isAdmin?: boolean) =>
			isAdmin
				? "Dynamic walls automatically update with your latest reviews. Upgrade to Growth plan to unlock dynamic walls and more features."
				: "Dynamic walls require a plan upgrade. Please contact your workspace admin to enable this feature.",
	},
	dynamic_widgets: {
		title: "Dynamic Widgets",
		message: (_limit?: number, isAdmin?: boolean) =>
			isAdmin
				? "Dynamic widgets automatically update with your latest reviews. Upgrade to Growth plan to unlock dynamic widgets and more features."
				: "Dynamic widgets require a plan upgrade. Please contact your workspace admin to enable this feature.",
	},
	reviews_per_wall_limit: {
		title: "Review Limit Per Wall",
		message: (limit?: number, isAdmin?: boolean) =>
			isAdmin
				? `Your current plan allows a maximum of ${limit ?? 2} reviews per wall. Upgrade to Growth plan to display more reviews.`
				: `This brand has reached the maximum reviews per wall limit. Please contact your workspace admin to upgrade.`,
	},
	reviews_per_widget_limit: {
		title: "Review Limit Per Widget",
		message: (limit?: number, isAdmin?: boolean) =>
			isAdmin
				? `Your current plan allows a maximum of ${limit ?? 2} reviews per widget. Upgrade to Growth plan to display more reviews.`
				: `This brand has reached the maximum reviews per widget limit. Please contact your workspace admin to upgrade.`,
	},
	custom_domain: {
		title: "Custom Domain",
		message: (_limit?: number, isAdmin?: boolean) =>
			isAdmin
				? "Use your own custom domain for your testimonial pages. Upgrade to Growth plan to build trust with your brand."
				: "To use custom domains, please contact your workspace admin to upgrade the plan.",
	},
	analytics: {
		title: "Advanced Analytics",
		message: (_limit?: number, isAdmin?: boolean) =>
			isAdmin
				? "Get detailed insights into your testimonials and forms. Upgrade to Growth plan for comprehensive analytics and reporting."
				: "To access advanced analytics, please contact your workspace admin to upgrade the plan.",
	},
	api_access: {
		title: "API Access",
		message: (_limit?: number, isAdmin?: boolean) =>
			isAdmin
				? "Integrate testimonials programmatically with our API. Upgrade to Growth plan to unlock full API access."
				: "To access the API, please contact your workspace admin to upgrade the plan.",
	},
	integrations: {
		title: "Premium Integrations",
		message: (_limit?: number, isAdmin?: boolean) =>
			isAdmin
				? "Connect with premium tools and services. Upgrade to Growth plan to access all integrations."
				: "To access premium integrations, please contact your workspace admin to upgrade the plan.",
	},
	branding_customization: {
		title: "Advanced Branding",
		message:(_limit?: number, isAdmin?: boolean) =>
			isAdmin
				? "Unlock full branding customization including custom colors, fonts, and logo uploads. Upgrade to Growth plan to create a unique brand identity."
				: "Advanced branding customization requires a plan upgrade. Please contact your workspace admin to unlock this feature.",
	},
	general: {
		title: "Upgrade to Growth",
		message: (_limit?: number, isAdmin?: boolean) =>
			isAdmin
				? "This feature is available on the Growth plan. Upgrade now to unlock all features and take your testimonials to the next level."
				: "This feature requires a plan upgrade. Please contact your workspace admin.",
	},
};

export default function UpgradeToGrowthModal({ open, onOpenChange, context = "general", customMessage, customTitle, workspaceId, reviewLimit, userRole, onMaybeLater }: UpgradeToProModalProps) {
	const navigate = useNavigate();
	const isAdmin = userRole === "admin";
	const contextData = CONTEXT_MESSAGES[context];
	const title = customTitle || contextData.title;
	const message = customMessage || contextData.message(reviewLimit, isAdmin);

	const handleUpgrade = () => {
		onOpenChange(false);
		const billingPath = workspaceId ? `/${workspaceId}/billing` : "/billing";
		navigate(billingPath);
	};

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 modal-overlay" />
				<Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-md modal-content overflow-hidden">
					{/* Icon */}
					<div className="flex items-center justify-center w-14 h-14 mx-auto mt-6 mb-4 rounded-full bg-blue-100">
						<svg className="w-7 h-7 text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
							<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
						</svg>
					</div>

					{/* Title */}
					<Dialog.Title className="text-lg font-semibold text-gray-900 text-center mb-2">{title}</Dialog.Title>

					{/* Description */}
					<Dialog.Description className="text-sm text-gray-600 text-center mb-6 px-6">{message}</Dialog.Description>

					{/* Benefits Section - Only show for admins */}
					{isAdmin && (
						<div className="mx-6 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
							<p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Growth Plan Includes</p>
							<ul className="space-y-2">
								{PLAN_GROWTH.filter((p) => p.planCardVisibilty === true).map((benefit, index) => (
									<li key={index} className="flex items-center gap-2 text-sm text-gray-700">
										<svg className="w-4 h-4 text-green-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
										</svg>
										<span>{benefit.text}</span>
									</li>
								))}
							</ul>
						</div>
					)}

					{/* Actions */}
					<div className="px-6 pb-6 space-y-3">
						{isAdmin ? (
							<>
								<Button variant="brand" className="w-full" onClick={handleUpgrade}>
									Upgrade to Growth
								</Button>
							<Button variant="light" className="w-full" onClick={() => onMaybeLater ? onMaybeLater() : onOpenChange(false)}>
								Maybe Later
							</Button>
							</>
						) : (
							<Button variant="light" className="w-full" onClick={() => onOpenChange(false)}>
								Close
							</Button>
						)}
					</div>

					{/* Close Button */}
					<Dialog.Close asChild>
						<button className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
							<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</Dialog.Close>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
